import { Shield, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    welcome: 'Welcome back, Founding Member',
    message: 'You believed in Say It NOW from the start — and that means more than you know. Your support helps keep this space alive for everyone.',
    badge: 'Founding Member',
    tagline: 'Your words carry extra meaning.',
  },
  es: {
    welcome: 'Bienvenido de vuelta, Miembro Fundador',
    message: 'Creíste en Say It NOW desde el principio — y eso significa más de lo que imaginas. Tu apoyo ayuda a mantener este espacio vivo para todos.',
    badge: 'Miembro Fundador',
    tagline: 'Tus palabras tienen un significado especial.',
  },
  fr: {
    welcome: 'Bon retour, Membre Fondateur',
    message: 'Vous avez cru en Say It NOW dès le début — et cela compte plus que vous ne le pensez. Votre soutien aide à garder cet espace vivant pour tous.',
    badge: 'Membre Fondateur',
    tagline: 'Vos mots portent une signification particulière.',
  },
  pt: {
    welcome: 'Bem-vindo de volta, Membro Fundador',
    message: 'Você acreditou no Say It NOW desde o início — e isso significa mais do que você imagina. Seu apoio ajuda a manter este espaço vivo para todos.',
    badge: 'Membro Fundador',
    tagline: 'Suas palavras carregam um significado especial.',
  },
  ar: {
    welcome: 'مرحباً بعودتك، عضو مؤسس',
    message: 'لقد آمنت بـ Say It NOW منذ البداية — وهذا يعني أكثر مما تتخيل. دعمك يساعد في الحفاظ على هذا المكان حياً للجميع.',
    badge: 'عضو مؤسس',
    tagline: 'كلماتك تحمل معنى خاصاً.',
  },
  ja: {
    welcome: 'おかえりなさい、創設メンバー',
    message: 'あなたは最初からSay It NOWを信じてくれました — それは想像以上に大きな意味があります。あなたのサポートがこの場を皆のために生かしています。',
    badge: '創設メンバー',
    tagline: 'あなたの言葉には特別な意味があります。',
  },
  ko: {
    welcome: '다시 오셨군요, 창립 멤버',
    message: 'Say It NOW를 처음부터 믿어주셨습니다 — 그것은 상상 이상으로 큰 의미입니다. 당신의 지지가 모두를 위한 이 공간을 살아있게 합니다.',
    badge: '창립 멤버',
    tagline: '당신의 말에는 특별한 의미가 담겨 있습니다.',
  },
  id: {
    welcome: 'Selamat datang kembali, Anggota Pendiri',
    message: 'Anda percaya pada Say It NOW sejak awal — dan itu berarti lebih dari yang Anda bayangkan. Dukungan Anda membantu menjaga ruang ini tetap hidup untuk semua orang.',
    badge: 'Anggota Pendiri',
    tagline: 'Kata-kata Anda membawa makna istimewa.',
  },

  hi: {
    welcome: 'वापस स्वागत है, Founding Member',
    message: 'आपने शुरुआत से ही Say It NOW पर विश्वास किया — और इसका मतलब जितना आप जानते हैं उससे कहीं ज़्यादा है। आपका समर्थन इस जगह को सभी के लिए जीवित रखने में मदद करता है।',
    badge: 'संस्थापक सदस्य',
    tagline: 'आपके शब्दों का अतिरिक्त अर्थ है।',
  },
};

interface FounderWelcomeCardProps {
  userName?: string;
  slotNumber?: number;
}

export function FounderWelcomeCard({ userName, slotNumber }: FounderWelcomeCardProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 shadow-soft">
        {/* Subtle glow accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/8 rounded-full blur-[60px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/8 rounded-full blur-[50px] pointer-events-none" />

        <CardContent className="relative p-6 md:p-8">
          <div className="flex items-start gap-4">
            {/* Shield icon */}
            <div className="shrink-0 flex items-center justify-center h-12 w-12 rounded-2xl bg-primary/10 border border-primary/20">
              <Shield className="h-6 w-6 text-primary" />
            </div>

            <div className="flex-1 min-w-0">
              {/* Badge chip */}
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                  <Shield className="h-3 w-3" />
                  {t.badge}
                  {slotNumber && (
                    <span className="text-primary/60 ml-0.5">#{slotNumber}</span>
                  )}
                </span>
              </div>

              {/* Welcome heading */}
              <h2 className="text-lg md:text-xl font-bold text-foreground mb-1.5">
                {t.welcome}{userName ? `, ${userName}` : ''}{' '}
                <Heart className="inline h-4 w-4 text-primary align-baseline" fill="currentColor" />
              </h2>

              {/* Gratitude message */}
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                {t.message}
              </p>

              {/* Tagline */}
              <p className="text-xs font-medium text-primary/70 italic">
                {t.tagline}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
